import { useCallback, useEffect, useState } from "react";
import Feature from "ol/Feature";
import LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import { DrawEvent } from "ol/interaction/Draw";
import CircleStyle from "ol/style/Circle";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import ZoomToLocation from "@avinet/adaptive-ui-maps/control/ZoomToLocation";
import Draw from "@avinet/adaptive-ui-maps/vector/interaction/Draw";
import VectorSource from "@avinet/adaptive-ui-maps/vector/source/VectorSource";
import VectorLayer from "@avinet/adaptive-ui-maps/vector/VectorLayer";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import useGeometryPicker from "../../hooks/useGeometryPicker";
import { Icon } from "../Icon";
import MapMouseMoveListener from "./MapMouseMoveListener";
import "./LineGeometryPicker.scss";

const style = new Style({
  stroke: new Stroke({ color: "#006699", width: 3 }),
});

const draftStyle = new Style({
  stroke: new Stroke({ color: "#0099ff", width: 3 }),
});

const draftPointStyle = new Style({
  image: new CircleStyle({
    fill: new Fill({ color: "#0099ff" }),
    stroke: new Stroke({ color: "#fff", width: 1 }),
    radius: 5,
  }),
});

interface LineGeometryPickerProps {
  value: string | null;
  onValueChange: (wkt: string | null) => void;
  onValidityChange: (isValid: boolean) => void;
  autoZoom?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  mapSrid?: string;
  srid?: string;
}

const LineGeometryPicker = ({
  value,
  onValueChange,
  onValidityChange,
  autoZoom,
  readOnly,
  disabled,
  mapSrid,
  srid,
}: LineGeometryPickerProps) => {
  const t = useTranslate("LineGeometryPicker");
  const { feature, onChange } = useGeometryPicker<LineString>({
    value,
    onValueChange,
    autoZoom,
    useLocationAsValue: false,
    mapSrid,
    srid,
  });

  const [draftFeature, setDraftFeature] = useState<Feature<LineString> | null>(
    null
  );
  const [draftFeatureVisible, setDraftFeatureVisible] = useState(true);
  const [draftPointFeature, setDraftPointFeature] =
    useState<Feature<Point> | null>(null);

  const onDrawEnd = useCallback(
    (e: DrawEvent) => {
      const point = (e.feature as Feature<Point>)
        ?.getGeometry()
        ?.getCoordinates();
      if (!point) return;

      let line = feature?.getGeometry()?.getCoordinates() ?? [];
      if (!Array.isArray(line)) {
        line = [line];
      }
      line.push(point);

      const nf = new Feature({ geometry: new LineString(line) });
      setDraftFeatureVisible(true);
      onChange(nf);
    },
    [feature, onChange]
  );

  const onMouseMove = useCallback(
    (end) => {
      if (!feature || !end) return;
      const coords = feature.getGeometry()?.getCoordinates();
      if (!coords || !coords.length) return;
      setDraftFeature(
        new Feature({
          geometry: new LineString([coords[coords.length - 1], end]),
        })
      );
    },
    [feature]
  );

  useEffect(() => {
    setDraftPointFeature(null);
    if (!feature) return;
    const coords = feature.getGeometry()?.getCoordinates();
    if (!coords || !coords.length) return;
    setDraftPointFeature(
      new Feature({
        geometry: new Point(coords[coords.length - 1]),
      })
    );
  }, [feature]);

  useEffect(() => {
    setDraftFeature(null);
    onValidityChange(
      (feature?.getGeometry()?.getCoordinates()?.length ?? 0) >= 2
    );
  }, [feature, onValidityChange]);

  const undo = useCallback(() => {
    const line = feature?.getGeometry()?.getCoordinates();
    if (!line) return;
    line.pop();
    if (line.length > 1)
      onChange(new Feature({ geometry: new LineString(line) }));
    else onChange(null);
  }, [feature, onChange]);

  return (
    <div className="geometry-picker geometry-picker-line">
      <ZoomToLocation title={t("location")} />
      <div className="edit-buttons">
        {feature && (
          <button
            type="button"
            className="btn map btn-reset warning"
            onClick={() => onChange(null)}
          >
            <Icon name="delete" /> {t("reset")}
          </button>
        )}
        {feature && (
          <button type="button" className="btn map btn-undo" onClick={undo}>
            <Icon name="subtractCircle" /> {t("undo")}
          </button>
        )}
        {draftFeatureVisible &&
          feature &&
          (feature.getGeometry()?.getCoordinates()?.length ?? 0) > 1 && (
            <button
              type="button"
              className="btn map btn-done"
              onClick={() => setDraftFeatureVisible(false)}
            >
              <Icon name="checkmark" /> {t("done")}
            </button>
          )}
      </div>
      <VectorLayer style={style} zIndex={1}>
        <VectorSource features={(feature && [feature]) || []} />
        {!readOnly && !disabled && <Draw type="Point" onDrawEnd={onDrawEnd} />}
      </VectorLayer>
      {draftFeatureVisible && (
        <>
          <VectorLayer style={draftStyle} zIndex={2}>
            <VectorSource features={(draftFeature && [draftFeature]) || []} />
          </VectorLayer>
          <VectorLayer style={draftPointStyle} zIndex={3}>
            <VectorSource
              features={(draftPointFeature && [draftPointFeature]) || []}
            />
          </VectorLayer>
          <MapMouseMoveListener onMouseMove={onMouseMove} />
        </>
      )}
    </div>
  );
};

export default LineGeometryPicker;
